import { CheckCircle2 } from 'lucide-react'
import type { DnsRecord, useAutoVerifyDomain } from '@/api/hooks/use-domains'
import { AutoConfigureCard } from './auto-configure-card'
import { DomainRecordTables } from './domain-record-tables'
import { VerificationCard } from './verification-card'

interface DomainVerifyStateProps {
  domainId: string
  domainName: string
  records: DnsRecord[]
  isVerified: boolean
  autoVerify: ReturnType<typeof useAutoVerifyDomain>
  onCheckNow: () => void
  onChangeDomain: () => void
  onNext: () => void
  onSkip: () => void
}

export function DomainVerifyState({
  domainId,
  domainName,
  records,
  isVerified,
  autoVerify,
  onCheckNow,
  onChangeDomain,
  onNext,
  onSkip,
}: DomainVerifyStateProps): React.JSX.Element {
  return (
    <div className="rounded-2xl border border-surface-3 bg-surface-0 p-8 lg:p-10 shadow-sm">
      <div className="flex flex-col gap-8">
        {/* Domain header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-text-primary">{domainName}</h2>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                isVerified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}
            >
              {isVerified ? 'Active' : 'Pending'}
            </span>
          </div>
          {!isVerified && (
            <button
              type="button"
              onClick={onChangeDomain}
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Change domain
            </button>
          )}
        </div>

        {/* Auto-configure card */}
        {!isVerified && domainId && (
          <AutoConfigureCard domainId={domainId} onComplete={onCheckNow} />
        )}

        {/* Divider */}
        <div className="flex items-center gap-4">
          <div className="h-px flex-1 bg-surface-3" />
          <span className="text-xs font-medium text-text-tertiary">Or add manually</span>
          <div className="h-px flex-1 bg-surface-3" />
        </div>

        {/* DNS record groups */}
        <DomainRecordTables records={records} />

        {/* Verification status card */}
        {!isVerified && (
          <VerificationCard
            records={records}
            isFetching={autoVerify.isFetching}
            dataUpdatedAt={autoVerify.dataUpdatedAt}
            onCheckNow={onCheckNow}
          />
        )}

        {/* DNS setup guide link */}
        <a
          href="https://docs.one-email.com/guides/dns-setup"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-text-tertiary hover:underline"
        >
          Need help? Read our DNS setup guide
        </a>

        {/* Success banner */}
        {isVerified && (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Domain verified successfully</span>
          </div>
        )}

        {/* Bottom actions */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onSkip}
            className="text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Skip for now
          </button>
          {isVerified ? (
            <button
              type="button"
              onClick={onNext}
              className="rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white
                hover:bg-brand-600 transition-colors"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={onNext}
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Continue anyway &rarr;
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
